import { developerErrorEvidence } from './developer-error-evidence.ts';

export type DeveloperLogValue = string | number | boolean | null | undefined;

export interface DeveloperLogField {
  label: string;
  value: DeveloperLogValue;
}

export interface DeveloperLogEnvironment {
  capturedAt: string;
  page: string;
  browser: string;
  buildCommit: string;
  builtAt: string;
}

const MAX_LOG_LENGTH = 24_000;
const MAX_VALUE_LENGTH = 4_000;

function boundedText(text: string, limit: number): string {
  const marker = '\n[truncated]';
  return text.length <= limit ? text : `${text.slice(0, limit - marker.length)}${marker}`;
}

function embeddedBuildValue(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : 'unavailable';
}

function currentPage(): string {
  if (typeof window === 'undefined') return 'unavailable';
  return `${window.location.origin}${window.location.pathname}`;
}

function currentEnvironment(): DeveloperLogEnvironment {
  return {
    capturedAt: new Date().toISOString(),
    page: currentPage(),
    browser: typeof navigator === 'undefined' ? 'unavailable' : navigator.userAgent,
    buildCommit: embeddedBuildValue(
      typeof __APP_BUILD_COMMIT_SHA__ === 'undefined' ? undefined : __APP_BUILD_COMMIT_SHA__,
    ),
    builtAt: embeddedBuildValue(
      typeof __APP_BUILD_TIMESTAMP__ === 'undefined' ? undefined : __APP_BUILD_TIMESTAMP__,
    ),
  };
}

// Keep diagnostic text, including database details, while removing credential shapes
// and bearer URL query data from the existing copyable report.
export function sanitizeDeveloperLogValue(value: DeveloperLogValue): string {
  const text = value == null ? 'unavailable' : String(value);
  const sanitized = text
    .replace(/https?:\/\/[^\s?#]+(?:\?[^\s#]*)?(?:#[^\s]*)?/gi, (url) => {
      try {
        const parsed = new URL(url);
        return `${parsed.origin}${parsed.pathname}`;
      } catch {
        return '[url redacted]';
      }
    })
    .replace(/\bBearer\s+[^\s]+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[jwt redacted]')
    .replace(
      /\b(api[_-]?key|authorization|password|secret|token)\s*[:=]\s*[^\s,;]+/gi,
      '$1=[redacted]',
    );
  return boundedText(sanitized, MAX_VALUE_LENGTH);
}

export function createDeveloperLog(
  error: unknown,
  fields: readonly DeveloperLogField[] = [],
  environment: DeveloperLogEnvironment = currentEnvironment(),
): string {
  const values: DeveloperLogField[] = [
    { label: 'Captured at (UTC)', value: environment.capturedAt },
    { label: 'Page', value: environment.page },
    { label: 'Build commit', value: environment.buildCommit },
    { label: 'Build time', value: environment.builtAt },
    { label: 'Browser', value: environment.browser },
    ...fields,
    ...developerErrorEvidence(error),
  ];
  const report = [
    'Forum & Lex developer log',
    ...values.map(
      ({ label, value }) =>
        `${sanitizeDeveloperLogValue(label)}: ${sanitizeDeveloperLogValue(value)}`,
    ),
  ].join('\n');
  return boundedText(report, MAX_LOG_LENGTH);
}
